import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { MessageSquare } from 'lucide-react';
import { LazyTooltip } from '../../../components/Tooltip';
import { Icon } from '../../../components/icons/Icon';
import { SaveSearchButton } from '../../../components/SaveSearchButton';
import { LastSeenSelector } from '../../../components/LastSeenSelector';
import CustomStats from '../../../seo_components/CustomStats';
import { useViewMode, type ViewMode } from '../hooks/useViewMode';
import type { IntlShape } from 'react-intl';
import type { SrpStaticStrings, LastSeenOptionValue } from '../../../features/srp/SrpViewModel';

export interface SearchSidebarProps {
  intl?: IntlShape;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentRefinement: string;
  handleSortChange: (value: string) => void;
  hasActiveSearch: boolean;
  onClearSearch: () => void;
  location: string | null;
  /** When set, company header is shown; hide duplicate stats row (e.g. "64 jobs at MongoDB"). */
  companyId?: string | null;
  className?: string;
  /** When provided (e.g. SRP static/island without SearchProvider), show simple job count instead of CustomStats. @deprecated Prefer staticStatsText + staticShowFinlandSubtitle for stable CustomStats display. */
  staticNbHits?: number;
  /** Pre-computed stats headline for CustomStats (static SRP); keeps CustomStats stable, only the string changes. */
  staticStatsText?: string;
  /** Whether to show Finland jobs subtitle when using staticStatsText. */
  staticShowFinlandSubtitle?: boolean;
  /** Pre-generated strings for static SRP (no IntlProvider). When provided, intl is not used. */
  staticStrings?: SrpStaticStrings;
  /** Resolved Last Seen option for static sidebar (from view model). */
  initialLastSeenOption?: LastSeenOptionValue;
  /** When provided, show feedback button on the stats row (right-aligned). */
  feedbackModalOpen?: boolean;
  setFeedbackModalOpen?: (open: boolean) => void;
}

/**
 * Search sidebar component - contains stats, view mode toggle, sort controls, and action buttons.
 * When staticStrings is provided, renders without useIntl (for static SRP).
 */
export const SearchSidebar: React.FC<SearchSidebarProps> = (props) => {
  if (props.staticStrings) {
    return <SearchSidebarWithStaticStrings {...props} />;
  }
  return <SearchSidebarWithIntl {...props} />;
};

const STATIC_LAST_SEEN_SELECT_CLASS = 'block min-w-[100px] @sm:min-w-[120px] @md:min-w-[140px] max-w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-2 @sm:px-3 py-2 text-xs @sm:text-sm font-medium text-gray-900 dark:text-gray-100 shadow-sm truncate cursor-default';

function SearchSidebarWithStaticStrings({
  viewMode,
  setViewMode,
  currentRefinement,
  handleSortChange,
  hasActiveSearch,
  onClearSearch,
  location,
  companyId = null,
  className = '',
  staticStatsText,
  staticShowFinlandSubtitle,
  staticStrings,
  initialLastSeenOption = 'all',
}: SearchSidebarProps & { staticStrings: SrpStaticStrings; initialLastSeenOption?: LastSeenOptionValue }) {
  const hideStatsRow = location || companyId;
  const isStaticSidebar = true;
  const sortMessages = staticStrings.sortLabels;
  const lastSeenValue = initialLastSeenOption ?? 'all';
  const getBaseIndexPrefix = (refinement: string): string => {
    if (refinement.endsWith('_postedDate_desc')) return refinement.replace('_postedDate_desc', '');
    if (refinement.endsWith('_salaryRangeMin_desc')) return refinement.replace('_salaryRangeMin_desc', '');
    return refinement;
  };
  const baseIndexPrefix = getBaseIndexPrefix(currentRefinement);
  const sortSegments = [
    { value: baseIndexPrefix, label: sortMessages.relevance },
    { value: `${baseIndexPrefix}_postedDate_desc`, label: sortMessages.newest },
    { value: `${baseIndexPrefix}_salaryRangeMin_desc`, label: sortMessages.salary },
  ];
  const iosSelectClass = 'px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer';

  return (
    <aside
      id="search-controls"
      className={`search-controls text-black dark:text-white px-2 @sm:px-2 @md:px-4 pb-2 @sm:pb-2 @md:pb-4 flex flex-col @sm:flex-col ${hasActiveSearch ? '' : '@[1600px]:flex-row @[1600px]:items-center @[1600px]:justify-between'} gap-2 @sm:gap-2 @md:gap-3 ${className}`}
      aria-label={staticStrings.controlsNavigation}
    >
      {/* Stats row: job count + Finland subtitle on the left, feedback button on the right (hidden when location or company header is shown) */}
      <div className={`flex items-start @[1600px]:items-center justify-between gap-3 w-full ${hasActiveSearch ? 'flex-1' : ''}`}>
        <div className={`min-w-0 break-words whitespace-normal ${hasActiveSearch ? 'flex flex-1' : 'flex-1'}`}>
          {!hideStatsRow && staticStatsText !== undefined && (
            <CustomStats statsText={staticStatsText} showFinlandSubtitle={staticShowFinlandSubtitle} finlandSubtitle={staticStrings.statsFinlandSubtitle} />
          )}
          {!hideStatsRow && staticStatsText === undefined && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{staticStrings.jobCountFormatted}</p>
          )}
        </div>
        {!hideStatsRow && (
          <span
            className="inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm cursor-default"
            aria-hidden
          >
            <MessageSquare className="w-4 h-4" />
            {staticStrings.feedbackButtonLabel}
          </span>
        )}
      </div>
      <div className={`flex flex-row flex-nowrap items-center gap-2 @sm:gap-2 @md:gap-4 justify-between w-full @sm:w-auto`}>
        <div className="hidden @[1600px]:flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1 shadow-inner border border-gray-200 dark:border-gray-700">
          <LazyTooltip content={staticStrings.tooltipViewList} position="bottom">
            <button type="button" className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer ${viewMode === 'list' ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400' : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'}`} onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'} aria-label={staticStrings.viewList}>
              <Icon name="bars4" useMaskForColor className="h-5 w-5" />
            </button>
          </LazyTooltip>
          <LazyTooltip content={staticStrings.tooltipViewMap} position="bottom">
            <button type="button" className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer ${viewMode === 'map' ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400' : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'}`} onClick={() => setViewMode('map')} aria-pressed={viewMode === 'map'} aria-label={staticStrings.viewMap}>
              <Icon name="mapPin" useMaskForColor className="h-5 w-5" />
            </button>
          </LazyTooltip>
        </div>
        <div className="flex @[1600px]:hidden rounded-xl bg-gray-100 dark:bg-gray-800 p-1 shadow-inner border border-gray-200 dark:border-gray-700">
          <button type="button" className={`px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer ${viewMode === 'list' ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400' : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'}`} onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'} aria-label={staticStrings.viewList} title={staticStrings.viewList}>
            <Icon name="bars4" useMaskForColor className="h-4 w-4" />
          </button>
          <button type="button" className={`px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer ${viewMode === 'map' ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400' : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'}`} onClick={() => setViewMode('map')} aria-pressed={viewMode === 'map'} aria-label={staticStrings.viewMap} title={staticStrings.viewMap}>
            <Icon name="mapPin" useMaskForColor className="h-4 w-4" />
          </button>
        </div>
        {viewMode !== 'map' && (
        <div className="flex @[1600px]:hidden items-center gap-1 @sm:gap-2">
          <select className={iosSelectClass} value={currentRefinement} onChange={(e) => handleSortChange(e.target.value)} aria-label={staticStrings.sortAriaLabel}>
            <option value={baseIndexPrefix}>{sortMessages.relevance}</option>
            <option value={`${baseIndexPrefix}_postedDate_desc`}>{sortMessages.newest}</option>
            <option value={`${baseIndexPrefix}_salaryRangeMin_desc`}>{sortMessages.salary}</option>
          </select>
          <select className={STATIC_LAST_SEEN_SELECT_CLASS} value={lastSeenValue} disabled aria-label={staticStrings.lastSeenAria} tabIndex={-1}>
            {staticStrings.lastSeenOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        )}
        <div className="hidden @[1600px]:flex items-center gap-2">
          {viewMode !== 'map' && (
            <div className="flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1 shadow-inner border border-gray-200 dark:border-gray-700">
              {sortSegments.map((seg) => (
                <button key={seg.value} type="button" className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 focus:z-10 cursor-pointer ${currentRefinement === seg.value ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400' : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'}`} onClick={() => handleSortChange(seg.value)} aria-pressed={currentRefinement === seg.value}>
                  {seg.label}
                </button>
              ))}
            </div>
          )}
          <select className={STATIC_LAST_SEEN_SELECT_CLASS} value={lastSeenValue} disabled aria-label={staticStrings.lastSeenAria} tabIndex={-1}>
            {staticStrings.lastSeenOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {hasActiveSearch && (
          <div className="flex gap-1 @sm:gap-2">
            <span
              className="backdrop-blur bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-full w-10 h-10 inline-flex items-center justify-center gap-0 @sm:gap-2 shadow @sm:w-auto @sm:h-auto @sm:rounded-lg @sm:px-3 @sm:py-1.5 @sm:text-sm cursor-default"
              aria-hidden
            >
              <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">
                <Icon name="arrowLongUp" useMaskForColor className="h-5 w-5" />
              </span>
              <span className="hidden @sm:inline text-black dark:text-white font-medium text-center">{staticStrings.saveSearchLabel}</span>
            </span>
            <button
              onClick={onClearSearch}
              className="backdrop-blur bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow hover:bg-gray-100 dark:hover:bg-gray-700 transition @sm:w-auto @sm:h-auto @sm:rounded-lg @sm:px-3 @sm:py-1.5 @sm:gap-2 @sm:text-sm cursor-pointer"
              aria-label={staticStrings.clearSearch}
              type="button"
            >
              <span className="text-gray-400 dark:text-gray-500">
                <Icon name="xMark" useMaskForColor className="h-5 w-5" />
              </span>
              <span className="hidden @sm:inline text-black dark:text-white font-medium">{staticStrings.clearSearch}</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function SearchSidebarWithIntl({
  intl,
  viewMode,
  setViewMode,
  currentRefinement,
  handleSortChange,
  hasActiveSearch,
  onClearSearch,
  location,
  companyId = null,
  className = '',
  staticNbHits,
  staticStatsText,
  staticShowFinlandSubtitle,
  feedbackModalOpen,
  setFeedbackModalOpen,
}: SearchSidebarProps) {
  const isStaticSidebar = staticStatsText !== undefined || staticNbHits != null;
  const hideStatsRow = location || companyId;

  const sortMessages = {
    relevance: intl!.formatMessage({
      id: 'sort.relevance',
      defaultMessage: 'Relevance',
      description: 'Sort option label to sort jobs by relevance to search query',
    }),
    newest: intl!.formatMessage({
      id: 'sort.newest',
      defaultMessage: 'Newest',
      description: 'Sort option label to sort jobs by newest first',
    }),
    salary: intl!.formatMessage({
      id: 'sort.salary',
      defaultMessage: 'Salary',
      description: 'Sort option label to sort jobs by salary (highest first)',
    }),
  };

  // Extract the base index prefix from currentRefinement
  // currentRefinement can be: "en_search", "en_search_postedDate_desc", "en_search_salaryRangeMin_desc", etc.
  // We need to remove the sort suffix if present
  const getBaseIndexPrefix = (refinement: string): string => {
    if (refinement.endsWith('_postedDate_desc')) {
      return refinement.replace('_postedDate_desc', '');
    }
    if (refinement.endsWith('_salaryRangeMin_desc')) {
      return refinement.replace('_salaryRangeMin_desc', '');
    }
    return refinement;
  };

  const baseIndexPrefix = getBaseIndexPrefix(currentRefinement);

  const sortSegments = [
    { value: baseIndexPrefix, label: sortMessages.relevance },
    { value: `${baseIndexPrefix}_postedDate_desc`, label: sortMessages.newest },
    { value: `${baseIndexPrefix}_salaryRangeMin_desc`, label: sortMessages.salary },
  ];

  const iosSelectClass = 'px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer';

  return (
    <aside
      id="search-controls"
      className={`search-controls text-black dark:text-white px-2 @sm:px-2 @md:px-4 pb-2 @sm:pb-2 @md:pb-4 flex flex-col @sm:flex-col ${hasActiveSearch ? '' : '@[1600px]:flex-row @[1600px]:items-center @[1600px]:justify-between'} gap-2 @sm:gap-2 @md:gap-3 ${className}`}
      aria-label={intl!.formatMessage({
        id: 'search.controls.navigation',
        defaultMessage: 'Search controls and filters',
        description: 'Accessibility label for search controls and filter section',
      })}
    >
      {/* Stats row: job count + Finland subtitle on the left, feedback button right-aligned (hidden when location or company header is shown) */}
      <div className={`flex items-start @[1600px]:items-center justify-between gap-3 w-full ${hasActiveSearch ? 'flex-1' : ''}`}>
        <div className={`min-w-0 break-words whitespace-normal ${hasActiveSearch ? 'flex flex-1' : 'flex-1'}`}>
          {!hideStatsRow
            ? (staticStatsText !== undefined
                ? (
                    <CustomStats
                      statsText={staticStatsText}
                      showFinlandSubtitle={staticShowFinlandSubtitle}
                    />
                  )
                : staticNbHits != null
                  ? (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <FormattedMessage
                          id="search.jobCount"
                          defaultMessage="{count, number} jobs"
                          description="Number of job results"
                          values={{ count: staticNbHits }}
                        />
                      </p>
                    )
                  : <CustomStats />)
            : null}
        </div>
        {!hideStatsRow && setFeedbackModalOpen != null && (
          <button
            type="button"
            onClick={() => setFeedbackModalOpen(true)}
            className="inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm cursor-pointer"
          >
            <MessageSquare className="w-4 h-4" />
            <FormattedMessage
              id="feedback.button.label"
              defaultMessage="Give us feedback"
              description="Button label to open feedback modal"
            />
          </button>
        )}
      </div>

      {/* Main content row - contains selectors and buttons */}
      <div className={`flex flex-row flex-nowrap items-center gap-2 @sm:gap-2 @md:gap-4 justify-between w-full @sm:w-auto`}>
        {/* View mode toggle (desktop-only) */}
        <div className="hidden @[1600px]:flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1 shadow-inner border border-gray-200 dark:border-gray-700">
          <LazyTooltip
            content={intl!.formatMessage({
              id: 'tooltip.view.list',
              defaultMessage: 'Switch to list view to see jobs in a compact list format',
              description: 'Tooltip text for list view button',
            })}
            position="bottom"
          >
            <button
              type="button"
              className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer ${
                viewMode === 'list'
                  ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400'
                  : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'
              }`}
              onClick={() => setViewMode('list')}
              aria-pressed={viewMode === 'list'}
              aria-label={intl!.formatMessage({
                id: 'view.list',
                defaultMessage: 'List view',
                description: 'Button aria-label to switch to list view layout',
              })}
            >
              <Icon name="bars4" useMaskForColor className="h-5 w-5" />
            </button>
          </LazyTooltip>
          <LazyTooltip
            content={intl!.formatMessage({
              id: 'tooltip.view.map',
              defaultMessage: 'Switch to map view to see job locations on a map',
              description: 'Tooltip text for map view button',
            })}
            position="bottom"
          >
            <button
              type="button"
              className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer ${
                viewMode === 'map'
                  ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400'
                  : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'
              }`}
              onClick={() => setViewMode('map')}
              aria-pressed={viewMode === 'map'}
              aria-label={intl!.formatMessage({
                id: 'view.map',
                defaultMessage: 'Map view',
                description: 'Button aria-label to switch to map view layout',
              })}
            >
              <Icon name="mapPin" useMaskForColor className="h-5 w-5" />
            </button>
          </LazyTooltip>
        </div>
        {/* View mode toggle for mobile/tablet */}
        <div className="flex @[1600px]:hidden rounded-xl bg-gray-100 dark:bg-gray-800 p-1 shadow-inner border border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className={`px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer ${
              viewMode === 'list'
                ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400'
                : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'
            }`}
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            aria-label={intl!.formatMessage({
              id: 'view.list',
              defaultMessage: 'List view',
              description: 'Button aria-label and title to switch to list view layout',
            })}
            title={intl!.formatMessage({
              id: 'view.list',
              defaultMessage: 'List view',
              description: 'Button aria-label and title to switch to list view layout',
            })}
          >
            <Icon name="bars4" useMaskForColor className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={`px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 cursor-pointer ${
              viewMode === 'map'
                ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400'
                : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'
            }`}
            onClick={() => setViewMode('map')}
            aria-pressed={viewMode === 'map'}
            aria-label={intl!.formatMessage({
              id: 'view.map',
              defaultMessage: 'Map view',
              description: 'Button aria-label and title to switch to map view layout',
            })}
            title={intl!.formatMessage({
              id: 'view.map',
              defaultMessage: 'Map view',
              description: 'Button aria-label and title to switch to map view layout',
            })}
          >
            <Icon name="mapPin" useMaskForColor className="h-4 w-4" />
          </button>
        </div>
        {/* iOS-style select for md and down - show sort only when not in map view */}
        <div className="flex @[1600px]:hidden items-center gap-1 @sm:gap-2">
          {viewMode !== 'map' && (
            <select
              className={iosSelectClass}
              value={currentRefinement}
              onChange={(e) => handleSortChange(e.target.value)}
              aria-label={intl!.formatMessage({
                id: 'sort.ariaLabel',
                defaultMessage: 'Sort results',
                description: 'Accessibility label for sort dropdown to change result ordering',
              })}
            >
              <option value={baseIndexPrefix}>{sortMessages.relevance}</option>
              <option value={`${baseIndexPrefix}_postedDate_desc`}>{sortMessages.newest}</option>
              <option value={`${baseIndexPrefix}_salaryRangeMin_desc`}>{sortMessages.salary}</option>
            </select>
          )}
          {!isStaticSidebar && <LastSeenSelector isMobile={true} />}
        </div>
        {/* iOS-style segmented control for lg and up - show sort only when not in map view */}
        <div className="hidden @[1600px]:flex items-center gap-2">
          {viewMode !== 'map' && (
            <div className="flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1 shadow-inner border border-gray-200 dark:border-gray-700">
              {sortSegments.map((seg) => (
                <button
                  key={seg.value}
                  type="button"
                  className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-green-400 focus:z-10 cursor-pointer ${
                    currentRefinement === seg.value
                      ? 'bg-gray-50 dark:bg-gray-900 text-green-600 shadow border border-green-400'
                      : 'bg-transparent text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent'
                  }`}
                  onClick={() => handleSortChange(seg.value)}
                  aria-pressed={currentRefinement === seg.value}
                >
                  {seg.label}
                </button>
              ))}
            </div>
          )}
          {!isStaticSidebar && <LastSeenSelector isMobile={false} />}
        </div>
        {/* Action buttons */}
        {hasActiveSearch && (
          <div className="flex gap-1 @sm:gap-2">
            {!isStaticSidebar && <SaveSearchButton />}
            <button
              onClick={onClearSearch}
              className="backdrop-blur bg-gray-50/60 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow hover:bg-gray-100 dark:hover:bg-gray-700 transition @sm:w-auto @sm:h-auto @sm:rounded-lg @sm:px-3 @sm:py-1.5 @sm:gap-2 @sm:text-sm cursor-pointer"
              aria-label={intl!.formatMessage({
                id: 'search.clear',
                defaultMessage: 'Clear Search',
                description: 'Button label to clear the search query and reset filters',
              })}
              type="button"
            >
              <span className="text-gray-400 dark:text-gray-500">
                <Icon name="xMark" useMaskForColor className="h-5 w-5" />
              </span>
              <span className="hidden @sm:inline text-black dark:text-white font-medium">
                <FormattedMessage
                  id="search.clear"
                  defaultMessage="Clear Search"
                  description="Button label to clear the search query and reset filters"
                />
              </span>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
